import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { programService, validateProgramName } from '#client/services/programs/programServices';
import { queryClient } from '#client/utils/queryClient.js';
import { SHARED_CONSTANTS } from '#shared/sharedConstants';
import { formatZodErrors, scrollToFirstError, parseSavedRules, toFormRuleGroups, toApiRuleGroups, mixedRequirementErrors, unsearchedRulesError } from '#client/utils/helpers';
import { validatePartialProgramSchema, validateWholeProgramSchema } from '#client/utils/forms/programs/clientProgramSchema';
import { getChangedFieldsDeep } from '#client/utils/helpers/programs/updateProgram';
import { PROGRAM_CONSTANTS } from '#client/pages/Programs/constants/programsConstants';
import { useProductFilterStore } from '#client/stores/productFilter/useProductFilterStore';

// Which products a set of rule groups matches; group names and Required/Optional don't affect it
/**
 * The rule groups a grouped program saves: the builder's groups as they are now. A save with rule
 * changes waits for Search Products (unsearchedRulesError), so these match the searched products.
 */
const ruleGroupsToSave = (productFilterStore) => (productFilterStore.ruleGroupsPayload ? toApiRuleGroups(productFilterStore.ruleGroups) : null);

// Groups joined with AND that mix Required and Optional, or rule changes that haven't been searched. Blocks saving.
const firstRequirementError = () => {
    const productFilterState = useProductFilterStore.getState();
    const { conditionType, ruleGroups } = productFilterState;
    const mixedError = conditionType === 'groupedRules' ? Object.values(mixedRequirementErrors(ruleGroups))[0] : undefined;
    return mixedError ?? unsearchedRulesError(productFilterState);
};

// A rule group error from the server, which no form field shows, so it becomes the toast message
const ruleGroupsErrorMessage = (error) => {
    const data = error.response?.data?.error;
    return [data, data?.error].find(Array.isArray)?.find((issue) => issue.path?.[0] === 'ruleGroups')?.message;
};

function buildProductOverridesMap(overrides = []) {
    return overrides.reduce((acc, override) => {
        if (!override?.productId) return acc;

        const key = override.variantId ? `variant-${override.variantId}` : `product-${override.productId}`;
        acc[key] = {
            productId: override.productId,
            ...(override.variantId && { variantId: override.variantId }),
            exclude: override.exclude || false,
            priceOverridden: override.priceOverridden || false,
            programPrice: override.programPrice ?? null,
            persisted: true,
        };

        return acc;
    }, {});
}

// Initial state for the program store
const initialProgramState = {
    // Program Mode State
    mode: 'view',
    programId: '',

    // Program Header State
    name: '',
    companyId: null,
    companyName: '',
    status: 'Active',
    programAdmins: [],

    // Program Info State
    dateStart: new Date(),
    dateExpires: new Date(),
    taxExempt: false,
    freeShipping: false,
    isInStoreOnly: true,
    requireId: false,

    // Program Details State
    discountAmount: '',
    discountType: '',
    subsidyType: '',
    maxItems: '',
    maxValue: '',
    maxUse: '',
    notificationThreshold: '',
    externallyManaged: false,
    firstNameRequired: false,
    lastNameRequired: false,
    employeeIdRequired: false,
    poNumberRequired: false,
    programDescription: '',

    // Program Products State (program-specific products, not general filtering)
    programProducts: [],

    // Product overrides/exclusions tracking (persists across pagination)
    // Key format: 'product-{productId}' for product-level, 'variant-{variantId}' for variant-level
    productOverrides: {}, // { 'product-{id}' | 'variant-{id}': { productId, variantId?, exclude, priceOverridden, programPrice } }

    // Product Table State
    productsLoadedAfterRulesChange: false,
    initialEligibleProductRules: '',
    overridePrices: {},
    sortSelected: ['product asc'],

    // Program Metrics State
    numberOfIssuedVouchers: 0,
    vouchersRedemptionRate: 0,
    totalMonetaryAmount: 0,
    totalNumberOfItemsRedeemed: 0,
    hasActiveVouchers: false,

    // Pagination State
    pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: null,
        endCursor: null,
    },
    currentPage: 1,
    isLoadingMore: false,

    // Toast State
    toastError: '',
    success: '',
    warning: '',
    isLoading: false,
    errors: {},
    // Track the latest products load to avoid stale resets
    productsLoadToken: 0,
    isProductsLoading: false,
    // Original Program Data once initialized
    // This is used to revert changes or compare with the current state
    originalProgram: null,
};

// Example to colocate stores
// https://zustand.docs.pmnd.rs/guides/flux-inspired-practice#colocate-store-actions

// Create the program store with combine middleware for better type inference
const useProgramStoreBase = create(
    combine(initialProgramState, (set, get) => ({
        // Actions for Program Mode
        setMode: (mode) => {
            set({ mode });
            // Also sync mode with product filter store
            useProductFilterStore.getState().setMode(mode);
        },

        // Actions for Program Header
        setName: (name) => set({ name }),
        setCompanyId: (companyId) => set({ companyId }),
        setCompanyName: (companyName) => set({ companyName }),
        setStatus: (status) => set({ status }),
        setProgramId: (programId) => set({ programId }),
        setProgramAdmins: (programAdmins) => {
            const currentProgramAdmins = get().programAdmins;
            set({ programAdmins: [...currentProgramAdmins, programAdmins] });
        },
        removeAllProgramAdmins: () => set({ programAdmins: [] }),
        removeProgramAdmin: (index) => {
            const currentProgramAdmins = get().programAdmins;
            const updatedAdmins = [...currentProgramAdmins];
            updatedAdmins.splice(index, 1);
            set({ programAdmins: updatedAdmins });
        },
        updateProgramAdmins: (programAdmins, index) => {
            const currentProgramAdmins = get().programAdmins;
            const updatedAdmins = [...currentProgramAdmins];
            updatedAdmins[index] = programAdmins;
            set({ programAdmins: updatedAdmins });
        },

        // Actions for Program Info
        setDateStart: (dateStart) => set({ dateStart }),
        setDateExpires: (dateExpires) => set({ dateExpires }),
        setTaxExempt: (taxExempt) => set({ taxExempt }),
        setFreeShipping: (freeShipping) => set({ freeShipping }),
        setIsInStoreOnly: (isInStoreOnly) => set({ isInStoreOnly }),
        setRequireId: (requireId) => set({ requireId }),

        // Actions for Program Details
        setDiscountAmount: (discountAmount) => set({ discountAmount }),
        setDiscountType: (discountType) => set({ discountType }),
        setSubsidyType: (subsidyType) => set({ subsidyType }),
        setMaxItems: (maxItems) => set({ maxItems }),
        setMaxValue: (maxValue) => set({ maxValue }),
        setMaxUse: (maxUse) => set({ maxUse }),
        setExternallyManaged: (externallyManaged) => set({ externallyManaged }),
        setFirstNameRequired: (firstNameRequired) => set({ firstNameRequired }),
        setLastNameRequired: (lastNameRequired) => set({ lastNameRequired }),
        setEmployeeIdRequired: (employeeIdRequired) => set({ employeeIdRequired }),
        setPoNumberRequired: (poNumberRequired) => set({ poNumberRequired }),
        setProgramDescription: (programDescription) => set({ programDescription }),
        setNotificationThreshold: (notificationThreshold) => set({ notificationThreshold }),

        // Actions for Product Filter (delegate to product filter store for compatibility)
        setRestrictToEligibleProducts: (restrictToEligibleProducts) => {
            useProductFilterStore.getState().setRestrictToEligibleProducts(restrictToEligibleProducts);
        },
        setEligibleProductRules: (eligibleProductRules) => {
            useProductFilterStore.getState().setEligibleProductRules(eligibleProductRules);
        },
        setConditionType: (conditionType) => {
            useProductFilterStore.getState().setConditionType(conditionType);
        },
        setAllProducts: (allProducts) => {
            useProductFilterStore.getState().setAllProducts(allProducts);
        },
        setFilters: (filters) => {
            useProductFilterStore.getState().setFilters(filters);
        },
        setIsProductsLoading: (isProductsLoading) => {
            useProductFilterStore.getState().setIsProductsLoading(isProductsLoading);
        },

        // Actions for Product Table
        setSortSelected: (sortSelected) => set({ sortSelected }),

        // Actions for Toast State
        setToastError: (toastError) => set({ toastError }),

        // Actions for Program Metrics (expects metrics object from API)
        // Actions for Program Metrics
        setProgramMetrics: (program) => {
            const metrics = program.metrics || {};
            const isSubsidyPercentage = program.subsidyType === PROGRAM_CONSTANTS.SUBSIDY_TYPES.PERCENTAGE_SUBSIDY;

            set({
                numberOfIssuedVouchers: metrics.numberOfIssuedVouchers,
                vouchersRedemptionRate: metrics.vouchersRedemptionRate,
                totalMonetaryAmount: isSubsidyPercentage ? 'N/A' : metrics.totalMonetaryAmount,
                totalNumberOfItemsRedeemed: metrics.totalItemsRedeemed,
            });
        },

        // Initialize program products from server response with pagination
        initializeProgramProducts: async (programId, mode) => {
            const productFilterStore = useProductFilterStore.getState();

            // Skip entirely for customer
            if (mode === 'customer') {
                productFilterStore.setAllProducts([]);
                productFilterStore.setIsProductsLoading(false);
                set({ programProducts: [] });
                return [];
            }

            // If creating, keep products empty and not loading
            if (!programId || mode === 'create') {
                productFilterStore.setAllProducts([]);
                productFilterStore.setIsProductsLoading(false);
                set({ programProducts: [] });
                return [];
            }

            // Start a new load and store a token to avoid stale resets
            const token = (get().productsLoadToken ?? 0) + 1;
            set({ productsLoadToken: token, programProducts: [] });
            productFilterStore.setIsProductsLoading(true);
            productFilterStore.setAllProducts([]);

            try {
                // Fetch first page for display and all saved overrides for future update payloads.
                const [resp, overridesResp] = await Promise.all([programService.fetchProgramProducts(programId, 1), programService.fetchProgramProductOverrides(programId)]);
                const { products = [], pageInfo } = resp.data || {};
                const savedOverrides = overridesResp.data?.overrides || [];
                const productOverrides = buildProductOverridesMap(savedOverrides);
                set({ productOverrides });

                if (!products || products.length === 0) {
                    set({
                        programProducts: [],
                        productOverrides,
                        overridePrices: {},
                        pageInfo: {
                            hasNextPage: false,
                            hasPreviousPage: false,
                            startCursor: null,
                            endCursor: null,
                        },
                        currentPage: 1,
                        productsLoadedAfterRulesChange: true,
                    });
                    productFilterStore.setIsProductsLoading(false);
                    return [];
                }

                // Build initial overridePrices for product-level overrides
                const initialOverridePrices = {};
                savedOverrides.forEach((override) => {
                    if (!override.variantId && override.priceOverridden && override.programPrice != null) {
                        initialOverridePrices[override.productId] = override.programPrice;
                    }
                });
                const productsWithOverrides = get().applyOverridesToProducts(products);

                set({
                    programProducts: productsWithOverrides,
                    overridePrices: initialOverridePrices,
                    productOverrides,
                    pageInfo: {
                        hasNextPage: pageInfo.hasNextPage,
                        hasPreviousPage: pageInfo.hasPreviousPage,
                        totalCount: pageInfo.totalCount,
                        totalPages: pageInfo.totalPages,
                        startCursor: null,
                        endCursor: null,
                    },
                    currentPage: pageInfo.currentPage,
                    productsLoadedAfterRulesChange: true,
                });

                // Update product filter store with the fetched products
                productFilterStore.setAllProducts(productsWithOverrides);

                // Also reflect the fetched products on the originalProgram for reset/compare flows
                set((state) => ({
                    originalProgram: state.originalProgram
                        ? {
                              ...state.originalProgram,
                              // Store first page in originalProgram
                              programProducts: productsWithOverrides,
                          }
                        : state.originalProgram,
                }));

                return productsWithOverrides;
            } catch (error) {
                set({ toastError: error?.response?.data?.message || error.message || 'Failed to load program products' });
                console.error('Failed to initialize program products:', error);
            } finally {
                // Only clear loading if this is still the latest load
                if (get().productsLoadToken === token) {
                    productFilterStore.setIsProductsLoading(false);
                }
            }
        },

        // Complex Actions (Business Logic)
        updateProgramState: (program, programMode) => {
            const productFilterStore = useProductFilterStore.getState();

            set({
                mode: programMode || 'view',
                programId: program.id || null,
                name: program.name || '',
                originalName: program.name || '',
                companyId: program.companyId || null,
                companyName: program.companyName || '',
                status: program.status,
                programAdmins: program.programAdmins || [],
                dateStart: new Date(program.dateStart),
                dateExpires: new Date(program.dateExpires),
                taxExempt: program.taxExempt || false,
                freeShipping: program.freeShipping || false,
                isInStoreOnly: program.isInStoreOnly ?? true,
                requireId: program.requireId || false,
                discountAmount: program.discountAmount?.toString() || '',
                discountType: program.discountType || '',
                subsidyType: program.subsidyType || '',
                maxItems: program.maxItems?.toString() || '',
                maxValue: program.maxValue?.toString() || '',
                maxUse: program.maxUse?.toString(),
                notificationThreshold: program.notificationThreshold?.toString() || '',
                externallyManaged: program.externallyManaged || false,
                firstNameRequired: program.firstNameRequired || false,
                lastNameRequired: program.lastNameRequired || false,
                employeeIdRequired: program.employeeIdRequired || false,
                poNumberRequired: program.poNumberRequired || false,
                programDescription: program.programDescription || '',
                initialEligibleProductRules: program.eligibleProductRules || '',
                hasActiveVouchers: program.hasActiveVouchers || false,
            });

            // Update product filter state
            productFilterStore.setRestrictToEligibleProducts(program.restrictToEligibleProducts);
            productFilterStore.setEligibleProductRules(program.eligibleProductRules || '');
            productFilterStore.setConditionType(program.conditionType || 'allConditions');
            // Grouped programs load their rule groups; the others keep loading from the rule string
            const isGrouped = program.conditionType === 'groupedRules';
            productFilterStore.setRuleGroups(isGrouped ? toFormRuleGroups(program.ruleGroups) : []);
            productFilterStore.setRuleGroupsPayload(isGrouped ? program.ruleGroups : null);
            productFilterStore.setEmptyRequiredChains(isGrouped ? (program.emptyRequiredChains ?? []) : []);
            productFilterStore.setSearchedConditionType(program.conditionType || 'allConditions');
            productFilterStore.setMode(programMode || 'view');

            // Update Metrics
            get().setProgramMetrics(program);

            // Keep original program data for comparison
            set({ originalProgram: get().buildOriginalProgramData(program) });
        },

        handlePageChange: async (newPage, searchParams = null) => {
            const programId = get().programId;
            const productFilterStore = useProductFilterStore.getState();

            try {
                set({ isLoadingMore: true });

                let resp;
                // Use search endpoint if filters are provided
                if (searchParams) {
                    resp = await programService.searchProgramProducts(programId, {
                        page: newPage,
                        ...searchParams,
                    });
                } else {
                    resp = await programService.fetchProgramProducts(programId, newPage);
                }

                const { products = [], pageInfo } = resp.data || {};

                // Apply stored overrides to fetched products
                const applyOverridesToProducts = get().applyOverridesToProducts;
                const productsWithOverrides = applyOverridesToProducts(products);

                // Update product filter store with new page products
                productFilterStore.setAllProducts(productsWithOverrides);

                set({
                    programProducts: productsWithOverrides,
                    currentPage: pageInfo?.currentPage || newPage,
                    pageInfo: {
                        hasNextPage: pageInfo?.hasNextPage || false,
                        hasPreviousPage: pageInfo?.hasPreviousPage || false,
                        totalCount: pageInfo?.totalCount || 0,
                        totalPages: pageInfo?.totalPages || 0,
                        startCursor: null,
                        endCursor: null,
                    },
                    isLoadingMore: false,
                });
            } catch (error) {
                console.error('Error fetching program products page:', error);
                set({ isLoadingMore: false, toastError: error.message });
            }
        },

        buildProgramData: () => {
            const state = get();
            const productFilterStore = useProductFilterStore.getState();

            // Build programProductOverrides array from stored overrides
            const programProductOverrides = [];

            Object.values(state.productOverrides).forEach((override) => {
                if (override.priceOverridden || override.exclude) {
                    const overrideData = {
                        productId: override.productId,
                        programPrice: override.priceOverridden ? Number(override.programPrice || 0) : null,
                        priceOverridden: override.priceOverridden || false,
                        exclude: override.exclude || false,
                    };

                    // Add variantId if this is a variant-level override
                    if (override.variantId) {
                        overrideData.variantId = override.variantId;
                    }

                    programProductOverrides.push(overrideData);
                }
            });

            const ruleGroups = productFilterStore.conditionType === 'groupedRules' ? ruleGroupsToSave(productFilterStore) : null;

            const data = {
                name: state.name?.trim(),
                originalName: state.originalName,
                programDescription: state.programDescription.trim(),
                taxExempt: state.taxExempt ? true : false,
                freeShipping: state.freeShipping ? true : false,
                isInStoreOnly: state.isInStoreOnly ?? true,
                restrictToEligibleProducts: productFilterStore.restrictToEligibleProducts ? true : false,
                eligibleProductRules: productFilterStore.eligibleProductRules,
                conditionType: productFilterStore.conditionType,
                ...(ruleGroups && { ruleGroups }),
                requireId: state.requireId ? true : false,
                discountAmount: state.discountAmount && state.discountAmount.trim() !== '' ? parseFloat(state.discountAmount) || 0 : 0,
                discountType: state.discountType,
                subsidyType: state.subsidyType,
                maxItems: state.maxItems && state.maxItems.trim() !== '' ? parseInt(state.maxItems) || 0 : 0,
                maxValue: state.maxValue && state.maxValue.trim() !== '' ? parseFloat(state.maxValue) || 0 : 0,
                maxUse: state.maxUse && state.maxUse.trim() !== '' ? parseInt(state.maxUse) || 0 : 0,
                notificationThreshold: state.notificationThreshold && state.notificationThreshold.trim() !== '' ? parseFloat(state.notificationThreshold) || 0 : 0,
                externallyManaged: state.externallyManaged ? true : false,
                firstNameRequired: state.firstNameRequired ? true : false,
                lastNameRequired: state.lastNameRequired ? true : false,
                employeeIdRequired: state.employeeIdRequired ? true : false,
                poNumberRequired: state.poNumberRequired ? true : false,
                status: state.status,
                dateStart: new Date(state.dateStart),
                dateExpires: new Date(state.dateExpires),
                companyId: Number(state.companyId),
                programAdmins: state.programAdmins.filter((admin) => admin && admin.trim() !== ''),
                // Include snapshot ID if available (from MultiProductFilter search)
                productSnapshotId: productFilterStore.snapshotId || null,
                // Include overrides array (only items with overrides/exclusions)
                programProductOverrides: programProductOverrides.length > 0 ? programProductOverrides : null,
            };

            return data;
        },

        buildOriginalProgramData: (originalProgram) => {
            if (!originalProgram) {
                return {};
            }

            return {
                ...originalProgram,
                restrictToEligibleProducts: originalProgram.restrictToEligibleProducts,
                maxItems: parseInt(originalProgram.maxItems) || 0,
                maxValue: parseFloat(originalProgram.maxValue) || 0,
                maxUse: parseInt(originalProgram.maxUse) || 0,
                dateStart: new Date(originalProgram.dateStart),
                dateExpires: new Date(originalProgram.dateExpires),
                products: (originalProgram.programProducts || []).map((product) => ({
                    variantId: product.variantId || '',
                    programPrice: Number(product.programPrice) || 0,
                    productId: product.productId,
                    productName: product.productName || '',
                    priceOverridden: product.priceOverridden || false,
                })),
            };
        },

        // Initialize store with fetched program data
        initializeWithProgram: async (programId, mode) => {
            if (!programId) return;

            const currentProgramID = get().programId;
            const productFilterStore = useProductFilterStore.getState();

            // If the current program ID matches and mode is 'edit', skip fetching
            // This prevents unnecessary re-fetching when already in edit mode
            if (currentProgramID === Number(programId) && mode === 'edit') return;

            // Set mode, reset errors and loading state
            set({
                programId: programId,
                isLoading: true,
                errors: {},
                mode: mode,
                programProducts: [],
                overridePrices: {},
            });
            productFilterStore.setAllProducts([]);

            try {
                const result = await programService.fetchProgram(programId, mode);
                if (!result?.success) {
                    throw new Error(result?.message || 'Failed to fetch program data');
                }

                const program = result.data.program;

                // Parse saved rules to get filters. A grouped program's rule string is only a display value, so its flat list comes from the groups
                const parsedFilters =
                    program.conditionType === 'groupedRules'
                        ? toFormRuleGroups(program.ruleGroups).flatMap((group) => group.filters)
                        : parseSavedRules(program.eligibleProductRules, program.conditionType).filters;

                // Set filters in product filter store before updating program state
                productFilterStore.setFilters(parsedFilters);
                productFilterStore.setOriginalFilters(parsedFilters);
                productFilterStore.setSearchedFilters(parsedFilters);
                productFilterStore.setEligibleProductRules(program.eligibleProductRules);

                // Hydrate full program
                get().updateProgramState(program, mode);

                return program;
            } catch (error) {
                set({ toastError: error.message || 'Failed to fetch program data' });
                throw error; // Re-throw for external error handling
            } finally {
                set({ isLoading: false });
            }
        },

        // Handle program creation
        handleCreateProgram: async (navigate) => {
            const state = get();
            if (state.isLoading) return;

            set({ isLoading: true, errors: {}, toastError: null });

            try {
                const requirementError = firstRequirementError();
                if (requirementError) {
                    set({ toastError: requirementError });
                    scrollToFirstError();
                    return;
                }

                const programData = get().buildProgramData();

                // Double-check name uniqueness
                const error = await validateProgramName(programData.name, 'create');
                if (error) {
                    set({ errors: { name: error } });
                    scrollToFirstError();
                    return;
                }

                const validation = validateWholeProgramSchema({
                    name: programData.name,
                    companyId: programData.companyId,
                    programAdmins: programData.programAdmins,
                    eligibleProductRules: programData.eligibleProductRules,
                    programDescription: programData.programDescription,
                    isInStoreOnly: programData.isInStoreOnly,
                    conditionType: programData.conditionType,
                    dateStart: new Date(programData.dateStart),
                    dateExpires: new Date(programData.dateExpires),
                    discountAmount: programData.discountAmount,
                    discountType: programData.discountType,
                    subsidyType: programData.subsidyType,
                    maxItems: programData.maxItems,
                    maxValue: programData.maxValue,
                    restrictToEligibleProducts: programData.restrictToEligibleProducts,
                    // New snapshot-based fields
                    productSnapshotId: programData.productSnapshotId,
                    programProductOverrides: programData.programProductOverrides,
                });

                if (!validation.success) {
                    set({ errors: validation.errors });
                    scrollToFirstError();
                    return;
                }

                const result = await programService.createProgram(programData);

                if (!result.success) {
                    if (result.error) {
                        const fieldErrors = formatZodErrors(result.error);
                        set({ errors: fieldErrors });
                        scrollToFirstError();
                    } else {
                        throw new Error(result.message || 'Failed to create program');
                    }
                    return;
                }

                // Clear snapshotId in product filter store after successful creation
                const productFilterStore = useProductFilterStore.getState();
                productFilterStore.setSnapshotId(null);

                set({ isLoading: false, errors: {}, success: 'Program created successfully!' });
                if (navigate) {
                    // Navigate to the newly created program view
                    navigate(`/program-management/view/${result.data.program.id}`, { viewTransition: true });
                }
                // scrroll to top
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {
                const ruleGroupsError = ruleGroupsErrorMessage(error);
                if (ruleGroupsError) {
                    set({ toastError: ruleGroupsError });
                    return;
                }
                if (error.response?.status === 400 && error.response?.data?.error && Array.isArray(error.response.data.error)) {
                    const fieldErrors = formatZodErrors(error.response.data.error);
                    set({ errors: fieldErrors });
                    scrollToFirstError();
                    return;
                }
                set({ toastError: error.message || 'Failed to create program' });
            } finally {
                set({ isLoading: false });
            }
        },

        // Handle program update
        handleUpdateProgram: async (programId, navigate) => {
            set({ isLoading: true, errors: {} });

            try {
                const requirementError = firstRequirementError();
                if (requirementError) {
                    set({ toastError: requirementError });
                    scrollToFirstError();
                    return;
                }

                const programData = get().buildProgramData();
                const originalProgram = get().originalProgram;
                const originalProgramData = get().buildOriginalProgramData(originalProgram);

                const changedFields = getChangedFieldsDeep(programData, originalProgramData, {
                    ignoreOrderKeys: ['programAdmins', 'programProductOverrides', 'restrictToEligibleProducts', 'productSnapshotId'],
                });

                if (Object.keys(changedFields).length === 0) {
                    set({ toastError: 'No changes to save.' });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    return;
                }

                // Only check uniqueness if the name changed AND it's not equal to the original
                if ('name' in changedFields) {
                    const error = await validateProgramName(programData.name, 'edit', originalProgramData.name);
                    if (error) {
                        set({ errors: { name: error } });
                        scrollToFirstError();
                        return;
                    }
                }
                const validation = validatePartialProgramSchema(changedFields);

                if (!validation.success) {
                    set({ errors: validation.errors });
                    scrollToFirstError();
                    return;
                }

                const result = await programService.updateProgram(programId, changedFields);

                if (!result.success) {
                    if (result.error) {
                        set({ errors: formatZodErrors(result.error) });
                        scrollToFirstError();
                        return;
                    }
                    throw new Error(result.message || 'Failed to update program');
                }

                // Clear snapshotId in product filter store after successful update
                const productFilterStore = useProductFilterStore.getState();
                productFilterStore.setSnapshotId(null);

                //get().updateProgramState(result.data.program);
                set({ isLoading: false, errors: {}, success: result.message || 'Program updated successfully!' });
                if (navigate) {
                    navigate(`/program-management/view/${programId}`, { viewTransition: true });
                }
                // Scroll to top after successful update
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {
                const ruleGroupsError = ruleGroupsErrorMessage(error);
                if (ruleGroupsError) {
                    set({ toastError: ruleGroupsError });
                    return;
                }
                if (error.response?.status === 400 && Array.isArray(error.response?.data?.error)) {
                    set({ errors: formatZodErrors(error.response.data.error) });
                    scrollToFirstError();
                    return;
                }
                set({ toastError: error.message || 'Failed to update program' });
            } finally {
                set({ isLoading: false });
            }
        },

        // Handle program cancellation
        handleCancelProgram: (navigate) => {
            const mode = get().mode;
            const originalProgram = get().originalProgram;
            const programId = get().programId;
            const productFilterStore = useProductFilterStore.getState();

            // Clear snapshotId in product filter store on cancel
            productFilterStore.setSnapshotId(null);

            if (mode === 'create') {
                if (navigate) {
                    navigate('/program-management', { viewTransition: true });
                }
                // Scroll to top after cancellation
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                // Revert to original program data
                if (originalProgram) {
                    get().updateProgramState(originalProgram);
                    productFilterStore.setAllProducts(originalProgram.programProducts || []);
                    set({
                        programProducts: (originalProgram.programProducts || []).slice(0, SHARED_CONSTANTS.MAX_PRODUCTS_PER_SHOPIFY_CALL),
                        pageInfo: originalProgram.pageInfo || {
                            hasNextPage: false,
                            hasPreviousPage: false,
                            startCursor: null,
                            endCursor: null,
                        },
                        currentPage: 1,
                    });
                    productFilterStore.setEligibleProductRules(originalProgram.eligibleProductRules || '');
                    productFilterStore.setFilters(originalProgram.originalFilters || []);
                    productFilterStore.setSearchedFilters(originalProgram.originalFilters || []);
                    productFilterStore.setConditionType(originalProgram.conditionType || 'allConditions');
                }
                // Ensure products query refetches when returning to view mode
                if (programId) {
                    try {
                        queryClient.invalidateQueries({ queryKey: ['program-products', programId] });
                    } catch (e) {
                        // no-op
                    }
                }
                if (navigate && programId) {
                    set({ isLoading: false, errors: {}, toastError: '', success: '', warning: '' });
                    navigate(`/program-management/view/${programId}`, { viewTransition: true });
                }
                // Scroll to top after cancellation
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },

        // Handle program submission (create or update)
        handleProgramSubmit: async (navigate) => {
            const mode = get().mode;
            const programId = get().programId;
            set({
                errors: {},
                toastError: '',
                success: '',
                warning: '',
            });

            if (mode === 'create') {
                await get().handleCreateProgram(navigate);
            } else {
                await get().handleUpdateProgram(programId, navigate);
            }
        },

        archiveProgram: async (programId) => {
            const response = await programService.archiveProgram(programId);

            if (response.success) {
                set({ mode: 'view', status: response.data.program.status, errors: {} });
            } else {
                set({ errors: { archive: response.message || 'Failed to archive program' } });
            }

            return response;
        },

        // Helper function to apply stored overrides to products
        applyOverridesToProducts: (products) => {
            const state = get();
            const { productOverrides } = state;

            return products.map((product) => {
                // Check for product-level override
                const productKey = `product-${product.id}`;
                const productOverride = productOverrides[productKey];

                // Apply product-level overrides if they exist
                const updatedProduct = productOverride
                    ? {
                          ...product,
                          exclude: productOverride.exclude || false,
                          priceOverridden: productOverride.priceOverridden || false,
                          programPrice: productOverride.priceOverridden ? productOverride.programPrice : null,
                      }
                    : product;

                // Apply variant-level overrides if variants exist
                if (Array.isArray(updatedProduct.variants) && updatedProduct.variants.length > 0) {
                    updatedProduct.variants = updatedProduct.variants.map((variant) => {
                        const variantKey = `variant-${variant.id}`;
                        const variantOverride = productOverrides[variantKey];

                        return variantOverride
                            ? {
                                  ...variant,
                                  exclude: variantOverride.exclude || false,
                                  priceOverridden: variantOverride.priceOverridden || false,
                                  programPrice: variantOverride.priceOverridden ? variantOverride.programPrice : null,
                              }
                            : variant;
                    });
                }

                return updatedProduct;
            });
        },

        // Unified function to update product/variant overrides (exclude and price)
        updateProductOverride: ({ productId, variantId, exclude, priceOverridden, programPrice }) => {
            const state = get();
            const productOverrides = { ...state.productOverrides };
            const productFilterStore = useProductFilterStore.getState();
            const allProducts = productFilterStore.allProducts;

            // Determine key and level
            const isVariant = !!variantId;
            const key = isVariant ? `variant-${variantId}` : `product-${productId}`;

            // Get existing override or create new
            const existingOverride = productOverrides[key] || {};

            // Merge updates
            const updatedOverride = {
                ...existingOverride,
                productId,
                ...(isVariant && { variantId }),
                exclude: exclude !== undefined ? exclude : existingOverride.exclude || false,
                priceOverridden: priceOverridden !== undefined ? priceOverridden : existingOverride.priceOverridden || false,
                programPrice: programPrice !== undefined ? programPrice : existingOverride.programPrice || null,
                isCleared: false,
            };

            if (!updatedOverride.exclude && !updatedOverride.priceOverridden) {
                if (existingOverride.persisted || existingOverride.isCleared) {
                    productOverrides[key] = {
                        ...updatedOverride,
                        programPrice: null,
                        isCleared: true,
                    };
                } else {
                    delete productOverrides[key];
                }
            } else {
                productOverrides[key] = updatedOverride;
            }

            set({ productOverrides });

            // Update UI in productFilterStore
            if (isVariant) {
                // Variant-level update
                const applyVariants = (product) => {
                    if (product.id !== productId || !Array.isArray(product.variants)) return product;
                    return {
                        ...product,
                        variants: product.variants.map((v) => {
                            if (v.id !== variantId) return v;
                            return {
                                ...v,
                                exclude: updatedOverride.exclude,
                                priceOverridden: updatedOverride.priceOverridden,
                                programPrice: updatedOverride.priceOverridden ? updatedOverride.programPrice : null,
                            };
                        }),
                    };
                };
                productFilterStore.setAllProducts(allProducts.map(applyVariants));
            } else {
                // Product-level update
                const applyProduct = (p) => {
                    if (p.id !== productId) return p;
                    return {
                        ...p,
                        exclude: updatedOverride.exclude,
                        priceOverridden: updatedOverride.priceOverridden,
                        programPrice: updatedOverride.priceOverridden ? updatedOverride.programPrice : null,
                    };
                };
                productFilterStore.setAllProducts(allProducts.map(applyProduct));
            }
        },

        // Reset only form data, keep UI state
        resetForm: async (mode) => {
            const currentState = get();
            set({
                ...initialProgramState,
                // Keep UI state
                toastError: currentState.toastError,
                success: currentState.success,
                warning: currentState.warning,
                isLoading: currentState.isLoading,
                errors: currentState.errors,
            });

            // Reset product filter store
            useProductFilterStore.getState().resetProductFilter();

            if (mode === 'create') {
                set({
                    mode: mode,
                    programId: null,
                });
            }

            return;
        },
    }))
);

// Auto-generated selectors function
// https://zustand.docs.pmnd.rs/guides/auto-generating-selectors
const createSelectors = (_store) => {
    let store = _store;
    store.use = {};

    for (let k of Object.keys(store.getState())) {
        store.use[k] = () => store((s) => s[k]);
    }

    return store;
};

// Create the store with auto-generated selectors
export const useProgramStore = createSelectors(useProgramStoreBase);

// Add product filter store selectors for backward compatibility
// These allow components to access product filter state through useProgramStore.use
// eslint-disable-next-line react-hooks/rules-of-hooks
useProgramStore.use.restrictToEligibleProducts = () => useProductFilterStore((s) => s.restrictToEligibleProducts);
// eslint-disable-next-line react-hooks/rules-of-hooks
useProgramStore.use.eligibleProductRules = () => useProductFilterStore((s) => s.eligibleProductRules);
// eslint-disable-next-line react-hooks/rules-of-hooks
useProgramStore.use.conditionType = () => useProductFilterStore((s) => s.conditionType);
// eslint-disable-next-line react-hooks/rules-of-hooks
useProgramStore.use.allProducts = () => useProductFilterStore((s) => s.allProducts);
// eslint-disable-next-line react-hooks/rules-of-hooks
useProgramStore.use.filters = () => useProductFilterStore((s) => s.filters);
// eslint-disable-next-line react-hooks/rules-of-hooks
useProgramStore.use.originalFilters = () => useProductFilterStore((s) => s.originalFilters);
// eslint-disable-next-line react-hooks/rules-of-hooks
useProgramStore.use.isProductsLoading = () => useProductFilterStore((s) => s.isProductsLoading);

// Example usage of the auto-generated selectors:
// const name = useProgramStore.use.name();
// const mode = useProgramStore.use.mode();
// const isLoading = useProgramStore.use.isLoading();

// Or you can use them directly:
// export const useName = () => useProgramStore.use.name();
// export const useMode = () => useProgramStore.use.mode();
// export const useIsLoading = () => useProgramStore.use.isLoading();

// This is an example of a store with no actions
// https://zustand.docs.pmnd.rs/guides/practice-with-no-store-actions
//export const setMode = (mode) => useProgramStore.setState({ mode });

/**
 * Selector hooks for optimized component rendering
 * Each hook uses useShallow to prevent unnecessary re-renders
 */

// Selector for program actions, used in components that need to perform actions on the program
// This includes actions like archiving, setting various program properties, and handling program creation and updates
export const useProgramActions = () =>
    useProgramStore(
        useShallow((state) => ({
            archiveProgram: state.archiveProgram,
            setAllProducts: state.setAllProducts,
            initializeProgramProducts: state.initializeProgramProducts,
            setName: state.setName,
            setCompanyId: state.setCompanyId,
            setCompanyName: state.setCompanyName,
            setEligibleProductRules: state.setEligibleProductRules,
            setConditionType: state.setConditionType,
            setStatus: state.setStatus,
            setProgramId: state.setProgramId,
            setProgramAdmins: state.setProgramAdmins,
            setNotificationThreshold: state.setNotificationThreshold,
            setDateStart: state.setDateStart,
            setDateExpires: state.setDateExpires,
            setTaxExempt: state.setTaxExempt,
            setRestrictToEligibleProducts: state.setRestrictToEligibleProducts,
            setFreeShipping: state.setFreeShipping,
            setIsInStoreOnly: state.setIsInStoreOnly,
            setFilters: state.setFilters,
            setRequireId: state.setRequireId,
            setDiscountAmount: state.setDiscountAmount,
            setDiscountType: state.setDiscountType,
            setSubsidyType: state.setSubsidyType,
            setMaxItems: state.setMaxItems,
            setMaxValue: state.setMaxValue,
            setMaxUse: state.setMaxUse,
            setMode: state.setMode,
            updateProductOverride: state.updateProductOverride,
            applyOverridesToProducts: state.applyOverridesToProducts,
            setToastError: state.setToastError,
            setIsProductsLoading: state.setIsProductsLoading,
            setExternallyManaged: state.setExternallyManaged,
            setFirstNameRequired: state.setFirstNameRequired,
            setLastNameRequired: state.setLastNameRequired,
            setEmployeeIdRequired: state.setEmployeeIdRequired,
            setPoNumberRequired: state.setPoNumberRequired,
            setProgramDescription: state.setProgramDescription,
            setSortSelected: state.setSortSelected,
            updateProgramState: state.updateProgramState,
            buildProgramData: state.buildProgramData,
            buildOriginalProgramData: state.buildOriginalProgramData,
            handleCreateProgram: state.handleCreateProgram,
            handleUpdateProgram: state.handleUpdateProgram,
            handleCancelProgram: state.handleCancelProgram,
            handlePageChange: state.handlePageChange,
            handleProgramSubmit: state.handleProgramSubmit,
            removeAllProgramAdmins: state.removeAllProgramAdmins,
            removeProgramAdmin: state.removeProgramAdmin,
            updateProgramAdmins: state.updateProgramAdmins,
            resetForm: state.resetForm,
            initializeWithProgram: state.initializeWithProgram,
        }))
    );

// Selector for program header information, used in components that need to display basic program details
export const useProgramHeader = () =>
    useProgramStore(
        useShallow((state) => ({
            mode: state.mode,
            name: state.name,
            originalName: state.originalName,
            companyId: state.companyId,
            companyName: state.companyName,
            programAdmins: state.programAdmins,
            status: state.status,
            notificationThreshold: state.notificationThreshold,
        }))
    );

// Selector for program information, used in components that need to access basic program details
export const useProgramInfo = () =>
    useProgramStore(
        useShallow((state) => ({
            mode: state.mode,
            dateStart: state.dateStart,
            dateExpires: state.dateExpires,
            taxExempt: state.taxExempt,
            freeShipping: state.freeShipping,
            isInStoreOnly: state.isInStoreOnly,
            requireId: state.requireId,
            errors: state.errors,
        }))
    );

// Selector for program details, used in components that need to access program configuration and requirements
export const useProgramDetails = () =>
    useProgramStore(
        useShallow((state) => ({
            mode: state.mode,
            discountAmount: state.discountAmount,
            discountType: state.discountType,
            subsidyType: state.subsidyType,
            maxItems: state.maxItems,
            maxValue: state.maxValue,
            maxUse: state.maxUse,
            externallyManaged: state.externallyManaged,
            firstNameRequired: state.firstNameRequired,
            lastNameRequired: state.lastNameRequired,
            employeeIdRequired: state.employeeIdRequired,
            poNumberRequired: state.poNumberRequired,
            programDescription: state.programDescription,
            hasActiveVouchers: state.hasActiveVouchers,
            errors: state.errors,
        }))
    );

// Selector for program metrics, used in components that need to display program statistics
export const useProgramMetrics = () =>
    useProgramStore(
        useShallow((state) => ({
            numberOfIssuedVouchers: state.numberOfIssuedVouchers,
            vouchersRedemptionRate: state.vouchersRedemptionRate,
            totalMonetaryAmount: state.totalMonetaryAmount,
            totalNumberOfItemsRedeemed: state.totalNumberOfItemsRedeemed,
        }))
    );

// Selector for multi-product filter state, used in components that need to access product filtering rules
// This now delegates to the product filter store
export const useMultiProductFilter = () => {
    const programMode = useProgramStore((state) => state.mode);
    const programErrors = useProgramStore((state) => state.errors);
    const filterState = useProductFilterStore(
        useShallow((state) => ({
            restrictToEligibleProducts: state.restrictToEligibleProducts,
            eligibleProductRules: state.eligibleProductRules,
            conditionType: state.conditionType,
            filters: state.filters,
            isProductsLoading: state.isProductsLoading,
        }))
    );

    return {
        mode: programMode,
        errors: programErrors,
        ...filterState,
    };
};

// Selector for product table data
export const useProductTable = () => {
    const programState = useProgramStore(
        useShallow((state) => ({
            mode: state.mode,
            products: state.programProducts,
            overridePrices: state.overridePrices,
            pageInfo: state.pageInfo,
            currentPage: state.currentPage,
            isLoadingMore: state.isLoadingMore,
            errors: state.errors,
            sortSelected: state.sortSelected,
            programId: state.programId,
            isProductsLoading: state.isProductsLoading,
            isLoading: state.isLoading,
            allProducts: state.allProducts,
        }))
    );

    const filterState = useProductFilterStore(
        useShallow((state) => ({
            allProducts: state.allProducts,
            isProductsLoading: state.isProductsLoading,
        }))
    );

    return {
        ...programState,
        ...filterState,
    };
};

// Selector for program toast messages, used in components that need to display toast notifications
// Get toast messages and actions to set them
export const useProgramToast = () =>
    useProgramStore(
        useShallow((state) => ({
            toastError: state.toastError,
            success: state.success,
            warning: state.warning,
            errors: state.errors,

            // Actions
            setToastError: state.setToastError,
        }))
    );

// Selector for program mode, used in components that need to know the current mode (create/edit/view) and can be used to set it
export const useProgramMode = () =>
    useProgramStore(
        useShallow((state) => ({
            mode: state.mode,
            setMode: state.setMode,
        }))
    );
